import React from 'react';

import { useAiRovoCurrentRouteConfigurationStore } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';

type Props = {
	/**
	 * The provider(s) to render unless a non-self nudge is already present in the
	 * shared configuration store. Typically a single nudge provider component.
	 */
	children: React.ReactNode;
	/**
	 * Nudge ids belonging to the wrapped provider that should be ignored when
	 * deciding whether to defer. Without this, the wrapper would always defer on
	 * the second render (when the wrapped provider has populated its own nudge).
	 *
	 * Example: `ownNudgeIds={['rovo-collab-teammates-fomo-nudge']}` when wrapping
	 * `<AiRovoFomoNudgeProvider />`.
	 */
	ownNudgeIds?: ReadonlyArray<string>;
};

/**
 * Render-time wrapper that defers (renders nothing) when any other nudge is
 * already present in the shared `aiRovoCurrentRouteConfigurationStore`. Use
 * this in `jiraRouteToConfigProvider`'s `nudges` slot to mark a nudge as
 * lower-priority than its sibling on the same route.
 *
 * IMPORTANT: place the deferring nudge LAST in the Fragment so the higher-
 * priority sibling's `useEffect` fires first and populates the store before
 * this wrapper makes its decision.
 *
 * Example:
 * ```tsx
 * nudges: () => (
 *     <>
 *         <AiRovoIssueViewNudgesProvider />
 *         <DeferIfNudgePresent ownNudgeIds={['rovo-collab-teammates-fomo-nudge']}>
 *             <AiRovoFomoNudgeProvider />
 *         </DeferIfNudgePresent>
 *     </>
 * ),
 * ```
 */
export const DeferIfNudgePresent = ({
	children,
	ownNudgeIds = [],
}: Props): React.JSX.Element | null => {
	const currentNudges = useAiRovoCurrentRouteConfigurationStore(
		(state) => state.configuration?.nudges,
	);

	const hasOtherNudge = currentNudges?.some((nudge) => !ownNudgeIds.includes(nudge.id));
	if (hasOtherNudge) {
		return null;
	}

	return <>{children}</>;
};
